// Browser notification service for SnapText

class NotificationService {
  constructor() {
    this.enabled = localStorage.getItem('notifications_enabled') !== 'false';
    this.lastNotificationTime = {};
    this.notificationCooldown = 3000; // 3 seconds between notifications for same conversation
  }

  // Live-read permission from the browser on every access — caching it
  // at module load (as this file used to) meant a user granting via
  // Chrome's padlock / site settings while the page was open never
  // flipped our in-memory state, so the "Enable notifications" banner
  // and the Settings checkbox stayed stuck. useNotificationPermission
  // hook pairs with this to re-render React when the browser dispatches
  // the permission 'change' event.
  get permission() {
    return 'Notification' in window ? Notification.permission : 'default';
  }

  // Check if notifications are supported
  isSupported() {
    return 'Notification' in window;
  }

  // Check if notifications are enabled by user
  isEnabled() {
    return this.enabled && this.permission === 'granted';
  }

  // Request permission from user
  async requestPermission() {
    if (!this.isSupported()) {
      return 'unsupported';
    }

    // Treat requestPermission() as an explicit opt-in: whether the browser
    // just granted or was already granted from a prior session, flip the
    // per-user enabled flag on. Without this, unchecking then re-checking
    // the Settings toggle short-circuited here and never cleared the
    // localStorage 'false' — checkbox re-rendered unchecked forever.
    if (this.permission === 'granted') {
      this.setEnabled(true);
      return 'granted';
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.setEnabled(true);
      }
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Enable/disable notifications
  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('notifications_enabled', enabled ? 'true' : 'false');
  }

  // Check if we should show notification (cooldown check)
  shouldShowNotification(conversationId) {
    const now = Date.now();
    const lastTime = this.lastNotificationTime[conversationId] || 0;

    if (now - lastTime < this.notificationCooldown) {
      return false;
    }

    this.lastNotificationTime[conversationId] = now;
    return true;
  }

  // Show a notification
  show(options) {
    if (!this.isEnabled()) {
      return null;
    }

    const {
      title,
      body,
      icon,
      tag,
      data,
      onClick,
      requireInteraction = false
    } = options;

    try {
      const notification = new Notification(title, {
        body,
        icon: icon || '/favicon.ico',
        tag: tag || `snaptext-${Date.now()}`,
        badge: '/favicon.ico',
        requireInteraction,
        data: data || {},
        silent: false
      });

      // Handle click event
      if (onClick) {
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          onClick(event);
          notification.close();
        };
      }

      // Auto-close after 10 seconds if not requiring interaction
      if (!requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 10000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  // Show notification for new message
  showNewMessage(message, conversationName) {
    if (!this.shouldShowNotification(message.conversation_id)) {
      return null;
    }

    const fromName = conversationName || message.from_number;
    const preview = message.message_text?.substring(0, 100) || 'New message';

    return this.show({
      title: `New message from ${fromName}`,
      body: preview,
      tag: `conversation-${message.conversation_id}`,
      data: {
        conversationId: message.conversation_id,
        messageId: message.id
      },
      onClick: () => {
        // Navigate to messages page and select conversation
        if (window.location.pathname !== '/messages') {
          window.location.href = '/messages';
        }

        // Store selected conversation in session storage for Messages page to pick up
        sessionStorage.setItem('selectConversationId', message.conversation_id);
      }
    });
  }

  // Show notification for multiple new messages
  showMultipleMessages(count, conversations) {
    return this.show({
      title: 'SnapText',
      body: `You have ${count} new message${count > 1 ? 's' : ''} from ${conversations.length} conversation${conversations.length > 1 ? 's' : ''}`,
      tag: 'multiple-messages',
      onClick: () => {
        window.location.href = '/messages';
      }
    });
  }

  // Test notification
  showTest() {
    return this.show({
      title: 'SnapText Notifications Enabled',
      body: 'You will now receive notifications for new messages',
      requireInteraction: false
    });
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
